/**
 * AdminVoiceCommandModal — global admin voice search & command palette.
 *
 * Admin clicks a mic icon in the AdminShell header, says "show me all falls
 * last week" → AI returns intent + params + redirect_url → modal shows the
 * resolved query + a "Go" button that navigates.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Mic, Square, Loader2, Sparkles, X, AlertTriangle, ArrowRight, Volume2 } from "lucide-react";
import useVoiceRecorder from "@/lib/useVoiceRecorder";

const INTENT_LABEL = {
  search_incidents: "Search incidents",
  search_reports: "Search shift reports",
  search_clients: "Search clients",
  search_caregivers: "Search caregivers",
  open_billing: "Open billing",
  open_schedule: "Open schedule",
  open_compliance: "Compliance overview",
  open_timecards: "Timecards",
  open_dashboard: "Dashboard",
  open_client: "Open client profile",
  unknown: "Unknown",
};

export default function AdminVoiceCommandModal({ open, onClose }) {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  const rec = useVoiceRecorder({
    endpoint: "/ai/voice-fill/admin-command",
    filenamePrefix: "cmd",
    limitMs: 30 * 1000,
    onResult: (data) => setResult(data),
  });

  const close = () => {
    rec.reset(); setResult(null); onClose?.();
  };

  const go = () => {
    if (result?.redirect_url) {
      navigate(result.redirect_url);
      close();
    }
  };

  if (!open) return null;

  const canGo = !!result?.redirect_url && result.intent !== "unknown";

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-24"
      data-testid="voice-command-modal">
      <div className="card-surface w-full max-w-lg overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#7C3AED1a", color: "#7C3AED" }}>
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">Voice command</h2>
              <p className="text-xs text-clinical-subtext">
                {rec.isIdle && !result && "Try: 'show me falls last week' or 'open Margaret's profile'"}
                {rec.isRecording && `Listening · ${rec.mmss}`}
                {rec.isProcessing && "Understanding…"}
                {result && (result.intent === "unknown" ? "Didn't catch that" : `Intent: ${INTENT_LABEL[result.intent]}`)}
              </p>
            </div>
          </div>
          <button type="button" onClick={close}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close" data-testid="voice-command-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {!result && (
            <div className="flex flex-col items-center gap-3 py-4">
              {rec.isIdle && (
                <div className="text-center space-y-2">
                  <Volume2 className="mx-auto h-10 w-10 text-clinical-subtext/50" />
                  <p className="text-xs text-clinical-subtext">
                    Tap to start. 30s limit per command.
                  </p>
                  <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50/60 p-3 text-left"
                    data-testid="voice-command-examples"
                  >
                    <div className="label-eyebrow text-purple-700 mb-1.5">Try saying</div>
                    <ul className="space-y-1.5 text-xs text-purple-900">
                      <li className="italic">"Show me all falls last week"</li>
                      <li className="italic">"Open Margaret's profile"</li>
                      <li className="italic">"Sarah's shift reports this month"</li>
                      <li className="italic">"Who has expiring TB tests?"</li>
                    </ul>
                  </div>
                </div>
              )}
              {rec.isRecording && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-ping" />
                    <div className="relative h-20 w-20 rounded-full bg-purple-500 flex items-center justify-center">
                      <Mic className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-2xl font-mono tabular-nums text-clinical-text" data-testid="voice-command-timer">
                    {rec.mmss}
                  </div>
                </div>
              )}
              {rec.isProcessing && <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />}
              <button
                type="button"
                onClick={rec.toggle}
                disabled={rec.isProcessing}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  rec.isRecording ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-clinical-accent text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={rec.isRecording ? "voice-command-stop" : "voice-command-start"}
              >
                {rec.isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {rec.isRecording ? "Stop" : rec.isProcessing ? "Processing…" : "Start"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="voice-command-results">
              {result.transcript && (
                <div className="rounded-lg bg-clinical-bg/60 p-3">
                  <div className="label-eyebrow mb-1">You said</div>
                  <p className="text-sm text-clinical-text">"{result.transcript}"</p>
                </div>
              )}

              {result.intent === "unknown" ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={14} /> Couldn't classify that command
                  </div>
                  <p>{result.summary}</p>
                </div>
              ) : (
                <div className="card-surface p-3 space-y-2">
                  <div>
                    <div className="label-eyebrow">Going to</div>
                    <p className="text-sm font-medium text-clinical-text mt-0.5">
                      {result.summary || INTENT_LABEL[result.intent]}
                    </p>
                  </div>
                  {result.matched_client_id && (
                    <div className="text-xs text-clinical-subtext">
                      ✓ Matched client: <code className="text-clinical-text">{result.matched_client_id.slice(0, 13)}…</code>
                    </div>
                  )}
                  {result.matched_caregiver_id && (
                    <div className="text-xs text-clinical-subtext">
                      ✓ Matched caregiver: <code className="text-clinical-text">{result.matched_caregiver_id.slice(0, 13)}…</code>
                    </div>
                  )}
                  {result.redirect_url && (
                    <div className="text-[11px] text-clinical-subtext font-mono">
                      → {result.redirect_url}
                    </div>
                  )}
                </div>
              )}

              {(result.warnings || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {result && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between">
            <button type="button" onClick={() => { setResult(null); rec.reset(); }}
              className="text-sm text-clinical-subtext hover:text-clinical-text"
              data-testid="voice-command-redo">
              Try again
            </button>
            {canGo && (
              <button type="button" onClick={go} className="btn-primary"
                data-testid="voice-command-go">
                Go <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
